import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import type { TFetchError } from '@/api/api-error.types';
import { getInvestWizardPath } from '@/api/investing/wizard-path';
import { _moveEntityInvestmentBoardEntry } from '@/api/investing-board/mutations';
import {
  _getEntityInvestmentBoard,
  _getEntityInvestmentBoardEntryDetail,
  _getMyInvestmentBoard,
  INVESTING_BOARD_QUERY_KEYS,
} from '@/api/investing-board/queries';
import { useToast } from '@/design-system';
import {
  isPipelinePermissionError,
  PipelineBoard,
  PipelineBoardRegion,
  PipelineBoardStack,
  PipelineBoardState,
  PipelineDetailDrawer,
  usePipelineUrlState,
} from '@/pages/pipelines/common';
import { PagePaths } from '@/routes/page-paths';
import { Permission, useCan } from '@/shared/modules/permissions';
import { useSelectedUserConfigSlice } from '@/stores/selectors';
import { getApiErrorMessage } from '@/utils/api-error-message';

import { useEntityBoardMove } from './hooks/use-entity-board-move';
import { ConfirmFundsReceivedDialog } from './modules/confirm-funds-received-dialog';
import { InvestingBoardEmptyState } from './modules/investing-board-empty-state';
import {
  mapEntityInvestmentBoardToPipelineBoard,
  mapMyInvestmentBoardToPipelineBoard,
} from './utils/map-board';
import { mapEntityEntryDetailToPipelineCardDetail } from './utils/map-entity-detail';

export const InvestingBoard = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { add: addToast } = useToast();
  const { selectedUserRole, selectedEntityId } = useSelectedUserConfigSlice();
  const isEntityScope = selectedUserRole === 'entity';
  // `MoveEntityInvestmentBoardEntryHandler` gates the drop on `EntityInvestmentBoard`, the same
  // permission that opens the board, so the drag is closed rather than letting a caller discover
  // the restriction from a 403 after the drop. The backend enforces it independently.
  const canMoveEntityCards = useCan(Permission.EntityInvestmentBoard);
  const entityCanDrag = isEntityScope && canMoveEntityCards;

  const myBoardQuery = useQuery({ ..._getMyInvestmentBoard(), enabled: !isEntityScope });
  const entityBoardQuery = useQuery({
    ..._getEntityInvestmentBoard(selectedEntityId),
    enabled: isEntityScope,
  });
  const activeQuery = isEntityScope ? entityBoardQuery : myBoardQuery;

  const board = useMemo(() => {
    if (isEntityScope) {
      return entityBoardQuery.data
        ? mapEntityInvestmentBoardToPipelineBoard(entityBoardQuery.data)
        : undefined;
    }
    return myBoardQuery.data ? mapMyInvestmentBoardToPipelineBoard(myBoardQuery.data) : undefined;
  }, [isEntityScope, entityBoardQuery.data, myBoardQuery.data]);

  // A null subscriptionId is a pre-subscription lead (a followed fund never subscribed to) —
  // there is no wizard session to resume, so the click falls back to the fund page instead.
  const handleInvestorCardClick = useCallback(
    (cardId: string) => {
      const entry = myBoardQuery.data?.columns
        .flatMap((column) => column.entries)
        .find((candidate) => candidate.id === cardId);
      if (!entry?.fund.slug) return;

      const TERMINAL_STAGES = new Set(['Rejected', 'NotEligible', 'Expired']);
      if (TERMINAL_STAGES.has(entry.stage)) {
        navigate(`${PagePaths.allFunds}/${entry.fund.slug}`);
        return;
      }

      if (entry.subscriptionId) {
        navigate(getInvestWizardPath(entry.fund.slug, entry.subscriptionId));
        return;
      }

      navigate(`${PagePaths.allFunds}/${entry.fund.slug}`);
    },
    [myBoardQuery.data, navigate],
  );

  const { selectedCardId, selectCard, closeCard } = usePipelineUrlState({
    decode: (params) => ({ card: params.get('card') ?? undefined }),
    encode: (state) => {
      const params = new URLSearchParams();
      if (state.card) params.set('card', state.card);
      return params;
    },
    sanitize: (state) => ({ card: state.card }),
    getActiveFilterCount: () => 0,
  });

  const entityDetailQuery = useQuery(
    _getEntityInvestmentBoardEntryDetail(isEntityScope ? selectedCardId : null),
  );
  const entityDetail = useMemo(
    () =>
      entityDetailQuery.data
        ? mapEntityEntryDetailToPipelineCardDetail(entityDetailQuery.data)
        : undefined,
    [entityDetailQuery.data],
  );

  const moveEntityCardMutation = useMutation({
    mutationFn: _moveEntityInvestmentBoardEntry,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [INVESTING_BOARD_QUERY_KEYS.entity, 'entity'],
      });
    },
    onError: (error) =>
      addToast({
        title: 'Could not move the card',
        description: getApiErrorMessage(error as TFetchError, 'Please try again.'),
        data: { status: 'error' },
      }),
  });

  const findEntityPledged = useCallback(
    (cardId: string) =>
      entityBoardQuery.data?.columns
        .flatMap((column) => column.entries)
        .find((entry) => entry.id === cardId)?.pledged ?? null,
    [entityBoardQuery.data],
  );

  const entityMove = useEntityBoardMove({
    moveEntityCard: moveEntityCardMutation.mutateAsync,
    findPledged: findEntityPledged,
    onRefuse: () =>
      addToast({
        title: 'Cannot confirm funds received',
        description: 'This card has no pledged amount to record a received amount against.',
        data: { status: 'error' },
      }),
  });

  const isBoardEmpty = Boolean(board && board.totals.cards === 0);

  return (
    <PipelineBoardStack>
      <PipelineBoardRegion>
        {isBoardEmpty ? (
          <InvestingBoardEmptyState isEntityScope={isEntityScope} />
        ) : (
          <PipelineBoardState
            board={board}
            loading={activeQuery.isLoading}
            permissionError={isPipelinePermissionError(activeQuery.error)}
            fetchPaused={activeQuery.fetchStatus === 'paused' && !activeQuery.data}
            error={activeQuery.isError}
            hasActiveFilters={false}
            onRetry={activeQuery.refetch}
          >
            {(readyBoard) => (
              <PipelineBoard
                board={readyBoard}
                // The investor board is permanently read-only; the entity board drags cards to move
                // them, with the confirm dialog gating the one write action (moving to Invested).
                dragEnabled={entityCanDrag}
                onMoveCard={entityCanDrag ? entityMove.onCardMove : undefined}
                movePending={
                  entityCanDrag
                    ? moveEntityCardMutation.isPending || Boolean(entityMove.pendingFundsReceived)
                    : undefined
                }
                onSelectCard={isEntityScope ? selectCard : handleInvestorCardClick}
              />
            )}
          </PipelineBoardState>
        )}
      </PipelineBoardRegion>

      {entityCanDrag && entityMove.pendingFundsReceived && (
        <ConfirmFundsReceivedDialog
          open
          pledged={entityMove.pendingFundsReceived.pledged}
          isSubmitting={moveEntityCardMutation.isPending}
          onConfirm={entityMove.confirm}
          onCancel={entityMove.cancel}
        />
      )}

      {isEntityScope && (
        <PipelineDetailDrawer
          open={Boolean(selectedCardId)}
          detail={entityDetail}
          loading={entityDetailQuery.isLoading}
          error={entityDetailQuery.isError}
          onClose={closeCard}
        />
      )}
    </PipelineBoardStack>
  );
};
